import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '../api/http';
import { isRefusal } from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';

/**
 * Non-lus de la messagerie de l'équipe pour la pastille de la navigation.
 * Requête à part de celles de l'écran « Messages » (clés « messaging… ») :
 * ses invalidations, ses purges et son rythme ne la concernent pas, et elle
 * ne change rien aux siens.
 */
const teamUnreadKey = (userId?: string) => ['team-unread', userId] as const;

/** Hors de l'écran « Messages », le compteur suit le rythme des échanges avec les élèves. */
const NAVIGATION_REFRESH_MS = 20_000;

/**
 * Messages de l'équipe non lus. Écran « Messages » fermé, le compteur se
 * rafraîchit seul ; ouvert, il ne demande rien : c'est l'écran qui lui
 * transmet son propre total (`useShareTeamUnread`).
 */
export function useTeamUnreadCount(messagesOpen: boolean): number {
  const userId = useAuthStore((s) => s.user?.id);
  const { data, error } = useQuery({
    queryKey: teamUnreadKey(userId),
    queryFn: async () => {
      const { data } = await http.get<{ unread?: unknown }>(
        '/v1/messaging/conversations',
      );
      // La pastille est sur tous les écrans : une réponse inattendue ne doit pas les faire tomber.
      return typeof data?.unread === 'number' ? data.unread : 0;
    },
    enabled: !messagesOpen,
    // Un refus (accès retiré) n'est pas redemandé en boucle ; une panne passagère, si.
    refetchInterval: (q) =>
      isRefusal(q.state.error) ? false : NAVIGATION_REFRESH_MS,
    retry: false,
    gcTime: 0,
  });
  return isRefusal(error) ? 0 : (data ?? 0);
}

/**
 * Appelé par l'écran « Messages » : sa liste, rafraîchie bien plus souvent,
 * tient la pastille à jour sans appel supplémentaire. `unread` reste
 * indéfini tant que l'écran n'a rien de sûr à annoncer.
 */
export function useShareTeamUnread(unread: number | undefined) {
  const userId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof unread === 'number')
      qc.setQueryData(teamUnreadKey(userId), unread);
  }, [qc, userId, unread]);
}
